import { useCallback, useEffect, useRef, useState } from 'react';
import { paymentsApi, paymentOrdersApi } from '../../../../api/bankApi';
import { extractApiArray, getApiErrorText } from '../../../../utils/apiResponse';
import { getOrderId, searchAttachmentOrders } from './paymentAttachmentOrders';

const getAvailableAmount = (payment, attachments) => {
  if ((payment.status?.databaseValue || payment.status) === 'USED') return 0;
  return Number(payment.availableAmount ?? Number(payment.amount) - attachments.reduce(
    (total, attachment) => total + Number(attachment.amount || 0), 0,
  ));
};

export default function usePaymentAttachment({ payment, onClose, onAttached, onComplete }) {
  const [details, setDetails] = useState({ payment, attachments: [], loading: true, error: '' });
  const [search, setSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState({
    items: [], loading: true, error: '', search: '', version: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchVersion, setSearchVersion] = useState(0);
  const mountedRef = useRef(false);
  const savingRef = useRef(false);
  const detailsRequestRef = useRef(0);
  const callbacksRef = useRef({ onClose, onAttached, onComplete });
  callbacksRef.current = { onClose, onAttached, onComplete };
  const availableAmount = getAvailableAmount(details.payment, details.attachments);
  const fullyUsed = (details.payment.status?.databaseValue || details.payment.status) === 'USED' || (
    !details.loading && !details.error && Number.isFinite(availableAmount) && availableAmount <= 0
  );

  const loadDetails = useCallback(async () => {
    const request = ++detailsRequestRef.current;
    setDetails(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const [paymentResponse, attachmentsResponse] = await Promise.all([
        paymentsApi.getById(payment.id),
        paymentOrdersApi.getPaymentAttachments(payment.id),
      ]);
      if (!mountedRef.current || request !== detailsRequestRef.current) return;
      const currentPayment = paymentResponse.data.data;
      if (!currentPayment || Number(currentPayment.id) !== Number(payment.id))
        throw new Error('Не вдалося оновити дані оплати');
      const attachments = extractApiArray(attachmentsResponse);
      setDetails({
        payment: currentPayment,
        attachments,
        loading: false,
        error: '',
      });
      return getAvailableAmount(currentPayment, attachments);
    } catch (error) {
      if (!mountedRef.current || request !== detailsRequestRef.current) return;
      setDetails(prev => ({
        ...prev,
        loading: false,
        error: getApiErrorText(error, 'Не вдалося завантажити оплату'),
      }));
    }
  }, [payment.id]);

  useEffect(() => {
    mountedRef.current = true;
    loadDetails();
    return () => {
      mountedRef.current = false;
      detailsRequestRef.current += 1;
    };
  }, [loadDetails]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const requestState = { search, version: searchVersion };
    setOrderSearch({ ...requestState, items: [], loading: true, error: '' });
    if (fullyUsed) return;
    const timeout = setTimeout(
      async () => {
        try {
          const result = await searchAttachmentOrders(search, controller.signal);
          if (active) {
            setOrderSearch({ ...requestState, items: result, loading: false, error: '' });
          }
        } catch (error) {
          if (active) {
            setOrderSearch({
              ...requestState, items: [], loading: false,
              error: getApiErrorText(error, 'Не вдалося завантажити замовлення'),
            });
          }
        }
      },
      search.trim() ? 300 : 0,
    );
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [search, searchVersion, fullyUsed]);

  // Hide previous results immediately, including the render before the search effect runs.
  const searchIsCurrent = orderSearch.search === search && orderSearch.version === searchVersion;
  const orders = searchIsCurrent ? orderSearch.items : [];
  const ordersLoading = !searchIsCurrent || orderSearch.loading;
  const ordersError = searchIsCurrent ? orderSearch.error : '';

  const canAttach =
    selectedOrder &&
    !fullyUsed &&
    !saving &&
    !details.loading &&
    !details.error &&
    Number.isFinite(availableAmount) &&
    availableAmount > 0;

  const handleAttach = async event => {
    event.preventDefault();
    if (!canAttach || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    setSuccess('');
    try {
      await paymentOrdersApi.attach(payment.id, getOrderId(selectedOrder), null);
      if (!mountedRef.current) return;
      const message = `Оплату прикріплено до замовлення #${getOrderId(selectedOrder)}`;
      setSelectedOrder(null);
      setSearch('');
      setSearchVersion(value => value + 1);
      const remainingAmount = await loadDetails();
      let warning = '';
      if (mountedRef.current && callbacksRef.current.onAttached) {
        try {
          await callbacksRef.current.onAttached(payment.id);
        } catch (error) {
          warning = 'Оплату прикріплено, але не вдалося оновити список оплат.';
        }
      }
      if (!mountedRef.current) return;
      if (Number.isFinite(remainingAmount) && remainingAmount <= 0) {
        callbacksRef.current.onComplete({ message, warning });
        callbacksRef.current.onClose();
      } else {
        setSuccess(message);
        setSaveError(warning);
      }
    } catch (error) {
      if (mountedRef.current) setSaveError(getApiErrorText(error, 'Не вдалося прикріпити оплату'));
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleClose = () => {
    if (!savingRef.current) callbacksRef.current.onClose();
  };
  const clearSelection = () => {
    setSelectedOrder(null);
    setSearch('');
    setSaveError('');
    setSearchVersion(value => value + 1);
  };

  const retrySearch = () => setSearchVersion(value => value + 1);
  const changeSearch = value => {
    setSearch(value);
    setSaveError('');
  };
  const selectOrder = order => {
    setSelectedOrder(order);
    setSaveError('');
  };
  return {
    details,
    search,
    orders,
    ordersLoading,
    ordersError,
    selectedOrder,
    saving,
    saveError,
    success,
    availableAmount,
    fullyUsed,
    canAttach,
    loadDetails,
    handleAttach,
    handleClose,
    clearSelection,
    retrySearch,
    changeSearch,
    selectOrder,
  };
}
